const CategoryItemList = require('@/lib/Items/CategoryItemList');
const CategoryItemListCollection = require('@/lib/Items/CategoryItemListCollection');
const ItemType = require('@/lib/Items/ItemType');
const { separateEventButtonsFromItems } = require('@/lib/Items/index');
const { sortByNavOrder } = require('@/functions/arraySorting');

/**
 * The SaleableItemService collects and retains all the different types of item that can be sold from the sell page.
 *
 * @constructor
 *
 * @param {CategoryRepository} categoryRepository
 * @param {GiftVoucherRepository} giftVoucherRepository
 * @param {MembershipPlanRepository} membershipPlanRepository
 * @param {ProductRepository} productRepository
 * @param {TicketRepository} ticketRepository
 * @param {UserService} userService
 */
const SaleableItemService = function (
    categoryRepository,
    giftVoucherRepository,
    membershipPlanRepository,
    productRepository,
    ticketRepository,
    userService
) {
    this.categoryRepository = categoryRepository;
    this.giftVoucherRepository = giftVoucherRepository;
    this.membershipPlanRepository = membershipPlanRepository;
    this.productRepository = productRepository;
    this.ticketRepository = ticketRepository;
    this.userService = userService;

    /**
     * @type {Object<string, CategoryItemListCollection>}
     */
    this.itemTypes = {};
};

SaleableItemService.prototype = {
    load() {
        return Promise.all([
            this.loadGiftVouchers(),
            this.loadMembershipPlans(),
            this.loadProducts(),
            this.loadTickets(),
        ]);
    },

    loadGiftVouchers() {
        return this.giftVoucherRepository
            .findActiveForBranch(this.userService.getItemBranchID())
            .then((giftVouchers) => {
                return this.createItemCollectionFromItems(giftVouchers).then((collection) => {
                    this.itemTypes[ItemType.GIFT_VOUCHER] = collection;
                });
            });
    },

    loadTickets() {
        return this.ticketRepository
            .findActiveForBranch(this.userService.getItemBranchID())
            .then((tickets) => {
                return this.createItemCollectionFromItems(tickets).then((collection) => {
                    this.itemTypes[ItemType.TICKET] = collection;
                });
            });
    },

    loadProducts() {
        return this.productRepository
            .findActiveForBranch(this.userService.getItemBranchID())
            .then((products) => {
                let foodProducts = products.filter((product) => product.isFood);
                let nonFoodProducts = products.filter((product) => !product.isFood);

                return Promise.all([
                    this.createItemCollectionFromItems(nonFoodProducts).then((collection) => {
                        this.itemTypes[ItemType.PRODUCT] = collection;
                    }),
                    this.createItemCollectionFromItems(foodProducts).then((collection) => {
                        this.itemTypes[ItemType.PRODUCT_FOOD] = collection;
                    }),
                ]);
            });
    },

    loadMembershipPlans() {
        return this.membershipPlanRepository
            .findActiveForBranch(this.userService.getItemBranchID())
            .then((membershipPlans) => {
                membershipPlans = membershipPlans.filter((membershipPlan) => membershipPlan.canBeSoldToday());

                return this.createItemCollectionFromItems(membershipPlans).then((collection) => {
                    this.itemTypes[ItemType.MEMBERSHIP_PLAN] = collection;
                });
            });
    },

    /**
     * Creates a CategoryItemListCollection from an array of items.
     * Organises items by category, and loads the category data from IndexedDB.
     *
     * @param {AbstractItem[]} items
     *
     * @return {Promise<CategoryItemListCollection>}
     */
    createItemCollectionFromItems(items) {
        let categoryItems = {};
        items.forEach((item) => {
            if (!categoryItems.hasOwnProperty(item.catID)) {
                categoryItems[item.catID] = [];
            }

            categoryItems[item.catID].push(item);
        });

        /**
         * @type {Category[]}
         */
        let categories = [];
        let categoryPromises = Object.keys(categoryItems).map((catID) => {
            return this.categoryRepository
                .find(catID)
                .then((category) => {
                    categories.push(category);
                })
                .catch((error) => {
                    console.warn(`Could not find category ${catID}`, error);
                });
        });

        return Promise
            .all(categoryPromises)
            .then(() => {
                const collection = new CategoryItemListCollection();

                sortByNavOrder(categories);

                categories.forEach((category) => {
                    const thisCategoryItemList = categoryItems[category.ID];

                    const { eventButtons, nonEventItems } = separateEventButtonsFromItems(thisCategoryItemList);

                    collection.add(
                        new CategoryItemList(category, eventButtons, nonEventItems)
                    );
                });

                return collection;
            });
    },

    /**
     * @param {Category[]} categories
     */
    sortCategories(categories) {
        // Apply ordering to the categories
        categories.sort(
            /**
             * Sort categories by navOrder then by name.
             *
             * @param {Category} a
             * @param {Category} b
             * @return {number}
             */
            function (a, b) {
                if (a.navOrder > b.navOrder) {
                    return 1;
                } else if (a.navOrder < b.navOrder) {
                    return -1;
                } else if (a.name !== b.name) {
                    return a.name > b.name ? 1 : -1;
                }
                return 0;
            }
        );
    },
};

module.exports = SaleableItemService;
